/**
 * ClientCalendarView Component
 * View-only calendar for client portal
 * Uses same API and structure as main calendar
 */

import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { AvatarGroup } from 'primereact/avatargroup';
import { Avatar } from 'primereact/avatar';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import multiMonthPlugin from '@fullcalendar/multimonth';
import listPlugin from '@fullcalendar/list';
import momentPlugin from '@fullcalendar/moment';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';

import { useClientCalendarUI } from '../context/ClientCalendarContext';
import { useCalendarEvents } from 'modules/calendarv2/hooks/useCalendarEvents';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { getUserTimezone, parseDateRange, getCurrentDateInTimezone } from 'modules/calendarv2/utils/timezoneHelpers';
import { isMoreLinkEvent } from 'modules/calendarv2/utils/eventFormatters';
import EventContent from 'modules/calendarv2/components/CalendarView/EventContent';
import ImageComponent from 'shared/ImageComponent';
import { getTenantDetails } from 'utils/utils';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import { getIconClass } from 'modules/calendarv2/utils/eventColorhelper';
import moment from 'moment';

const ClientCalendarView = () => {
  // UI actions
  const { openEventViewModal, openDayEventsPopup } = useClientCalendarUI();

  const userContext = useUserDetailsContext();
  const timezone = getUserTimezone(userContext);
  const [tenantId] = getTenantDetails();

  const [viewDateState, setViewDateState] = useState({
    currentView: 'dayGridMonth',
    currentDate: moment().tz(timezone).format(),
    dateRange: null,
  });

  const { currentView, currentDate, dateRange } = viewDateState || {};

  // Ref to access FullCalendar API
  const calendarRef = useRef(null);

  // Track if view is transitioning
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Fetch events using React Query (same as main calendar)
  const { events, isLoading, isError, error } = useCalendarEvents({
    view: currentView,
    dateRange,
    filters: {},
    selectedUsers: [], // Client view doesn't filter by users
    timezone,
    contact_id: userContext?.userDetails?.contact_id,
    enabled: Boolean(userContext?.userDetails?.contact_id),
  });

  // Handle view change from FullCalendar toolbar
  const handleViewChange = useCallback(() => setIsTransitioning(false), [currentView]);

  // Custom Year button click handler
  const handleYearButtonClick = useCallback(() => {
    if (currentView === 'multiMonthYear') return;

    setIsTransitioning(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) {
          calendarApi.changeView('multiMonthYear');
        }
      });
    });
  }, [currentView]);

  // Handle view changes
  const handleDatesSet = useCallback(
    (dateInfo) => {
      const range = parseDateRange(dateInfo, timezone);
      setViewDateState((prev) => ({ ...prev, currentView: dateInfo.view.type, dateRange: range }));
    },
    [timezone, setViewDateState]
  );

  // Handle event click - open view modal
  const handleEventClick = useCallback(
    (clickInfo) => {
      const event = clickInfo.event;

      // For more link events, open day events popup
      if (isMoreLinkEvent(event)) {
        openDayEventsPopup(event?.extendedProps?.date?.slice(0, 10));
        return;
      }

      // For normal events, open view modal
      openEventViewModal(event);
    },
    [openEventViewModal, openDayEventsPopup]
  );

  // Handle date select - disabled for client view
  const handleDateSelect = useCallback((selectInfo) => {
    // If clicking day number in month view, switch to day view
    if (selectInfo?.jsEvent?.target?.classList?.contains('fc-daygrid-day-number')) {
      const calendarApi = selectInfo.view.calendar;
      calendarApi.changeView('timeGridDay', selectInfo.startStr);
      return;
    }
    // Otherwise, do nothing (no event creation for clients)
  }, []);

  // Memoize event content renderer (same as main calendar)
  const eventContentRenderer = useCallback(
    (eventInfo) => {
      if (currentView === 'listWeek') {
        const { event } = eventInfo;
        const extendedProps = event.extendedProps || {};
        const attendees = extendedProps.participants || [];
        const attendeeCount = Array.isArray(attendees) ? attendees.length : 0;

        // Check if we're on mobile/small screen
        const isMobile = window.innerWidth < 768;

        if (isMobile) {
          // Mobile card layout
          return (
            <div className="fc-list-event-mobile-card">
              <div className="fc-list-event-card-header">
                <div className="fc-list-event-indicator" style={{ backgroundColor: event.backgroundColor || '#517dda' }}></div>
                <div className="fc-list-event-card-title">
                  <i className={`${getIconClass(extendedProps.calendar_type)} me-2`} style={{ color: 'var(--black-500)' }}></i>
                  {extendedProps.calendar_type === 'case'
                    ? 'Statute Of Limitations'
                    : extendedProps.calendar_type === 'user'
                    ? 'Birthday: ' + extendedProps.contact_name
                    : event.title}
                </div>
                <div className="fc-list-event-card-time">{event.allDay ? 'All Day' : `${eventInfo.timeText}`}</div>
              </div>
              <div className="fc-list-event-card-content">
                <div className="fc-list-event-card-row">
                  <div className="fc-list-event-card-label">Case:</div>
                  <div className="fc-list-event-card-value">{extendedProps.case_name || 'N/A'}</div>
                </div>
                {extendedProps.practiceArea && (
                  <div className="fc-list-event-card-row">
                    <div className="fc-list-event-card-label">Practice Area:</div>
                    <div className="fc-list-event-card-value">{extendedProps.practiceArea}</div>
                  </div>
                )}
                {extendedProps.event_type && (
                  <div className="fc-list-event-card-row">
                    <div className="fc-list-event-card-label">Type:</div>
                    <div className="fc-list-event-card-value">{extendedProps.event_type}</div>
                  </div>
                )}
                {extendedProps.due_date && (
                  <div className="fc-list-event-card-row">
                    <div className="fc-list-event-card-label">Due Date:</div>
                    <div className="fc-list-event-card-value">{convertDateFromApiToUserTimezone(extendedProps.due_date, timezone)}</div>
                  </div>
                )}
                <div className="fc-list-event-card-row">
                  <div className="fc-list-event-card-label">Attendees:</div>
                  <div className="fc-list-event-card-value">
                    {attendeeCount > 0 ? (
                      <>
                        <div className="attendees-count-mobile">
                          {attendeeCount} {attendeeCount === 1 ? 'person' : 'people'}
                        </div>
                        <div className="attendees-names-mobile">
                          <AvatarGroup>
                            {attendees.map((user, i) => {
                              if (i < 4) {
                                const stableId = `${i}-${user?.contact_id || user?.firm_user_id || i}`;
                                return (
                                  <ImageComponent
                                    key={stableId}
                                    filePath={
                                      user?.contact_id || user?.firm_user_id
                                        ? `${tenantId}/${user?.contact_id ? user?.contact_id : user?.firm_user_id}/profile_image`
                                        : null
                                    }
                                    fileName={user.name}
                                    className={`me-1 task-avatar assignee-${stableId}`}
                                    toolTipTarget={`.assignee-${stableId}`}
                                    toolTipContent={
                                      <>
                                        {user?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                                        {user.name}
                                      </>
                                    }
                                    tooltipPosition="top"
                                    showTooltip={true}
                                  />
                                );
                              }
                              return null;
                            })}
                            {attendees.length > 4 && <Avatar label={`+${attendees.length - 4}`} className="me-1 pointer" shape="circle" />}
                          </AvatarGroup>
                        </div>
                      </>
                    ) : (
                      <div>No attendees</div>
                    )}
                  </div>
                </div>
                <div className="fc-list-event-card-row">
                  <div className="fc-list-event-card-label">Location:</div>
                  <div className="fc-list-event-card-value">{extendedProps.location || 'N/A'}</div>
                </div>
              </div>
            </div>
          );
        }

        // Desktop table layout
        return (
          <div className="fc-list-event-custom-wrapper">
            <div className="fc-list-event-custom-cell event-cell">
              <div className="fc-list-event-indicator" style={{ backgroundColor: event.backgroundColor || '#517dda' }}></div>
              <div className="fc-list-event-details">
                <div className="fc-list-event-title">
                  <i className={`${getIconClass(extendedProps.calendar_type)} me-2`} style={{ color: 'var(--black-500)' }}></i>
                  {extendedProps.calendar_type === 'case'
                    ? 'Statute Of Limitations'
                    : extendedProps.calendar_type === 'user'
                    ? 'Birthday: ' + extendedProps.contact_name
                    : event.title}
                </div>
                <div className="d-flex align-items-center flex-wrap gap-3">
                  {extendedProps.case_name && (
                    <div className="fc-list-event-subtitle">
                      Case: <span className="text-bold">{extendedProps.case_name}</span>
                    </div>
                  )}
                  {extendedProps.practiceArea && (
                    <div className="fc-list-event-subtitle">
                      Practice area: <span className="text-bold">{extendedProps.practiceArea}</span>
                    </div>
                  )}
                  {extendedProps.event_type && (
                    <div className="fc-list-event-subtitle">
                      Type: <span className="text-bold">{extendedProps.event_type}</span>
                    </div>
                  )}
                  {extendedProps.due_date && (
                    <div className="fc-list-event-subtitle">
                      Due date: <span className="text-bold">{convertDateFromApiToUserTimezone(extendedProps.due_date, timezone)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="fc-list-event-custom-cell attendees-cell">
              {attendeeCount > 0 ? (
                <>
                  <div className="attendees-count">
                    {attendeeCount} {attendeeCount === 1 ? 'person' : 'people'}
                  </div>
                  <div className="attendees-names">
                    <AvatarGroup>
                      {attendees.map((user, i) => {
                        if (i < 4) {
                          const stableId = `${i}-${user?.contact_id || user?.firm_user_id || i}`;
                          return (
                            <ImageComponent
                              key={stableId}
                              filePath={
                                user?.contact_id || user?.firm_user_id
                                  ? `${tenantId}/${user?.contact_id ? user?.contact_id : user?.firm_user_id}/profile_image`
                                  : null
                              }
                              fileName={user.name}
                              className={`me-1 task-avatar assignee-${stableId}`}
                              toolTipTarget={`.assignee-${stableId}`}
                              toolTipContent={
                                <>
                                  {user?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                                  {user.name}
                                </>
                              }
                              tooltipPosition="top"
                              showTooltip={true}
                            />
                          );
                        }
                        return null;
                      })}
                      {attendees.length > 4 && <Avatar label={`+${attendees.length - 4}`} className="me-1 pointer" shape="circle" />}
                    </AvatarGroup>
                  </div>
                </>
              ) : (
                <div className="attendees-names">No attendees</div>
              )}
            </div>
            <div className="fc-list-event-custom-cell location-cell">{extendedProps.location || 'N/A'}</div>
          </div>
        );
      }

      if (currentView === 'multiMonthYear') {
        if (isMoreLinkEvent(eventInfo.event)) {
          const onClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            openDayEventsPopup(eventInfo?.event?._def?.extendedProps?.date?.slice(0, 10));
          };
          return (
            <a onClick={onClick} title={eventInfo.event.title} className="fc-daygrid-more-link fc-more-link custom-more-link">
              {eventInfo.event.title}
            </a>
          );
        }
        return <></>;
      }

      return <EventContent eventInfo={eventInfo} openDayEventsPopup={openDayEventsPopup} />;
    },
    [currentView, timezone, tenantId, openDayEventsPopup]
  );

  const getNowInUserTimezone = useCallback(() => {
    return getCurrentDateInTimezone(timezone);
  }, [timezone]);

  // Static options that never change
  const staticCalendarOptions = useMemo(
    () => ({
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, multiMonthPlugin, listPlugin, momentPlugin, momentTimezonePlugin],
      moreLinkClick: 'day',
      dayMaxEventRows: false,
      dayMaxEvents: false,
      eventMaxStack: Infinity,
      headerToolbar: {
        start: 'today prev,next',
        center: 'title',
        end: 'timeGridDay,timeGridWeek,dayGridMonth,customYearButton,listWeek',
      },
      buttonText: {
        today: 'Today',
        day: 'Day',
        week: 'Week',
        month: 'Month',
        list: 'Agenda',
      },
      allDayText: 'All Day',
      height: 'auto',
      editable: false, // View-only for client
      selectable: true, // Allow date selection for day view change
      selectMirror: false, // Disable mirror for client view
      nowIndicator: true,
      moreLinkContent: () => <></>,
      displayEventTime: true,
      displayEventEnd: true,
      eventTimeFormat: {
        hour: 'numeric',
        minute: '2-digit',
        meridiem: 'short',
      },
      slotLabelFormat: {
        hour: 'numeric',
        minute: '2-digit',
        meridiem: 'short',
      },
    }),
    []
  );

  // Custom buttons
  const customButtons = useMemo(
    () => ({
      customYearButton: {
        text: 'Year',
        click: handleYearButtonClick,
      },
    }),
    [handleYearButtonClick]
  );

  // View-specific configuration
  const viewsConfig = useMemo(
    () => ({
      listWeek: {
        type: 'list',
        duration: { days: 7 },
        titleFormat: { month: 'short', day: 'numeric', year: 'numeric' },
        listDayFormat: 'D dddd MMM, YYYY',
        listDaySideFormat: false,
        eventTimeFormat: {
          hour: 'numeric',
          minute: '2-digit',
          meridiem: 'short',
        },
      },
      multiMonthYear: {
        dayMaxEvents: false,
        dayMaxEventRows: false,
        eventDisplay: 'block',
        multiMonthMaxColumns: 2,
      },
    }),
    []
  );

  // Event handlers config
  const eventHandlers = useMemo(
    () => ({
      datesSet: handleDatesSet,
      eventClick: handleEventClick,
      select: handleDateSelect,
      viewDidMount: handleViewChange,
    }),
    [handleDatesSet, handleEventClick, handleDateSelect, handleViewChange]
  );

  // Final merged calendar options
  const calendarOptions = useMemo(
    () => ({
      ...staticCalendarOptions,
      customButtons,
      views: viewsConfig,
      initialView: currentView,
      initialDate: currentDate,
      timeZone: timezone,
      now: getNowInUserTimezone,
      events: events,
      eventContent: eventContentRenderer,
      ...eventHandlers,
    }),
    [
      staticCalendarOptions,
      customButtons,
      viewsConfig,
      currentView,
      currentDate,
      timezone,
      getNowInUserTimezone,
      events,
      eventContentRenderer,
      eventHandlers,
    ]
  );

  // Manually update FullCalendar events when they change
  useEffect(() => {
    if (calendarRef.current && Array.isArray(events)) {
      const calendarApi = calendarRef.current.getApi();

      // Get current event sources and remove them
      const eventSources = calendarApi.getEventSources();
      eventSources.forEach((source) => source.remove());

      // Remove any remaining events
      const currentEvents = calendarApi.getEvents();
      currentEvents.forEach((event) => event.remove());

      // Add new events as a batch
      calendarApi.addEventSource(events);
    }
  }, [events]);

  // Manage customYearButton active state
  useEffect(() => {
    if (calendarRef.current) {
      const calendarEl = calendarRef.current.getApi().el;
      const yearButton = calendarEl.querySelector('.fc-customYearButton-button');

      if (yearButton) {
        if (currentView === 'multiMonthYear') {
          yearButton.classList.add('fc-button-active');
        } else {
          yearButton.classList.remove('fc-button-active');
        }
      }
    }
  }, [currentView]);

  // Error state
  if (isError) {
    return (
      <div className="calendar-loading w-100 h-100">
        <i className="pi pi-exclamation-triangle" style={{ fontSize: '2rem', color: '#ef4444' }}></i>
        <p className="mb-0 ms-2 F-size16 text-bold">Error loading calendar: {error?.message || 'Unknown error'}</p>
      </div>
    );
  }

  // Show unified loading overlay
  const showLoading = isLoading || isTransitioning;
  const loadingMessage = isTransitioning ? 'Switching view...' : 'Loading calendar...';

  return (
    <div className="calendar-v2-view">
      {showLoading && (
        <div className="calendar-transition-overlay visible">
          <i className="pi pi-spin pi-spinner"></i>
          <p>{loadingMessage}</p>
        </div>
      )}
      <FullCalendar ref={calendarRef} {...calendarOptions} />
    </div>
  );
};

export default React.memo(ClientCalendarView);
